// src/components/snapshot/SnapshotFreemiumCard.tsx

import React from "react";
import { Lock, LucideIcon } from "lucide-react";

export type SnapshotContentType =
  | "list"
  | "benchmark"
  | "sample_insights"
  | "roi_themes"
  | "coaching"
  | "locked";

export interface SnapshotCardConfig {
  id: string;
  icon: React.ReactNode | LucideIcon;
  title: string;
  description?: string;
  contentType: SnapshotContentType;
  listItems?: string[];
  benchmarkData?: { label: string; value: number; color?: string }[];
  locked?: boolean;
  primaryCtaLabel?: string;
  onPrimaryClick?: () => void;
  secondaryCtaLabel?: string;
  onSecondaryClick?: () => void;
  // New: Item-level click handlers
  onListItemClick?: (index: number) => void;
  onBenchmarkClick?: (index: number) => void;
  onBarClick?: (index: number) => void;
}

export function SnapshotFreemiumCard({
  icon,
  title,
  description,
  contentType,
  listItems = [],
  benchmarkData = [],
  locked = false,
  primaryCtaLabel,
  onPrimaryClick,
  secondaryCtaLabel,
  onSecondaryClick,
  onListItemClick,
  onBenchmarkClick,
  onBarClick,
}: SnapshotCardConfig) {

  const renderContent = () => {
    switch (contentType) {
      case "list":
        return (
          <ul className="space-y-2">
            {listItems.map((item, idx) => (
              <li 
                key={idx} 
                className={`text-[11px] text-dvaTextSecondary flex items-start leading-snug ${
                  onListItemClick ? 'cursor-pointer hover:text-dvaOrange transition-colors' : ''
                }`}
                onClick={() => onListItemClick?.(idx)}
              >
                <span className="mr-2 text-dvaNeutral">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );

      case "benchmark":
        return (
          <div className="space-y-3">
            {benchmarkData.map((benchmark, idx) => (
              <div 
                key={idx}
                className={onBenchmarkClick ? 'cursor-pointer group' : ''}
                onClick={() => onBenchmarkClick?.(idx)}
              >
                <div className="flex justify-between text-[10px] text-dvaTextSecondary mb-1.5">
                  <span className={onBenchmarkClick ? 'group-hover:text-dvaOrange transition-colors' : ''}>
                    {benchmark.label}
                  </span>
                  <span className="text-dvaNavy font-semibold">
                    {benchmark.value}%
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out ${
                      onBenchmarkClick ? 'group-hover:opacity-80' : ''
                    }`}
                    style={{
                      width: `${benchmark.value}%`,
                      backgroundColor: benchmark.color || "#C4CDD7",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case "sample_insights":
        return (
          <div className="space-y-2">
            {listItems.map((item, idx) => (
              <div
                key={idx}
                className={`rounded-lg border border-[#E2E8F0] bg-[#F7FAFC] px-3 py-2 text-[11px] text-dvaTextSecondary leading-snug ${
                  onListItemClick ? 'cursor-pointer hover:border-dvaOrange hover:bg-orange-50 transition-all' : ''
                }`}
                onClick={() => onListItemClick?.(idx)}
              >
                {item}
              </div>
            ))}
          </div>
        );

      case "roi_themes":
        return (
          <div className="space-y-2">
            <div className="flex items-end justify-between h-24 gap-2 px-1">
              {/* Cool neutral palette - softened navy for better balance */}
              {[
                { height: "65%", color: "#A3B1C2" },
                { height: "85%", color: "#7B8FA3" },
                { height: "55%", color: "#2A3E54" },
                { height: "75%", color: "#CBD5E1" }
              ].map((bar, idx) => (
                <div 
                  key={idx}
                  className={`flex-1 rounded-t transition-all duration-500 ${
                    onBarClick ? 'cursor-pointer hover:opacity-70' : ''
                  }`}
                  style={{ 
                    height: bar.height,
                    backgroundColor: bar.color
                  }}
                  onClick={() => onBarClick?.(idx)}
                />
              ))}
            </div>
            <p className="text-[10px] text-dvaTextSecondary text-center mt-2 leading-snug">
              AI/ML, Population Health, Care Coordination, Revenue Cycle
            </p>
          </div>
        );

      case "coaching":
        return (
          <ul className="space-y-2">
            {listItems.map((item, idx) => (
              <li key={idx} className="text-[11px] text-dvaTextSecondary flex items-start leading-snug">
                <span className="mr-2 text-dvaNeutral font-semibold">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );

      case "locked":
        return (
          <div className="text-center py-4 text-xs text-dvaTextSecondary">
            <Lock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p>Pro feature — upgrade to unlock</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={[
        "relative flex h-full flex-col rounded-2xl bg-white p-5 shadow-sm transition-all duration-150",
        locked
          ? "ring-1 ring-[#E2E8F0] bg-gradient-to-b from-white to-dvaCoolGrayLight"
          : "ring-1 ring-[#D9DFEA]",
        "hover:shadow-md hover:-translate-y-0.5",
      ].join(" ")}
    >
      {/* Locked pill */}
      {locked && (
        <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-red-600 shadow-sm ring-1 ring-red-100">
          <Lock className="h-3 w-3" />
          <span>Pro</span>
        </div>
      )}

      {/* Header: Icon + Title + Description */}
      <div className="mb-4 flex items-start gap-3">
        {/* Icon container - icons come pre-styled with white */}
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-dvaNavy text-white">
          {icon}
        </div>

        {/* Title and description */}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-dvaNavy leading-snug mb-1">
            {title}
          </h3>
          {description && (
            <p className="text-xs text-dvaTextSecondary leading-snug">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Content area - blurred if locked */}
      <div className={`flex-1 ${locked ? "blur-sm" : ""}`}>
        {renderContent()}
      </div>

      {/* CTA area */}
      {(primaryCtaLabel || secondaryCtaLabel) && (
        <div className="mt-auto pt-4 space-y-2">
          {primaryCtaLabel && (
            <button
              onClick={onPrimaryClick}
              className="w-full inline-flex items-center justify-center rounded-xl bg-dvaOrange px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-[#ff5533] focus:outline-none focus:ring-2 focus:ring-dvaOrange focus:ring-offset-2 transition-all"
            >
              {primaryCtaLabel}
            </button>
          )}
          {secondaryCtaLabel && (
            <button
              onClick={onSecondaryClick}
              className="w-full text-[11px] font-semibold text-dvaNavy hover:text-dvaOrange transition-colors"
            >
              {secondaryCtaLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}